import React, { useEffect, useRef, useState } from "react";
import JobCodePicker from "./JobCodePicker";
import "./ClockInJobCodeSheet.css";

/**
 * ClockInJobCodeSheet — pre-clock-in bottom sheet for picking a job code.
 *
 * TIME-006 §3.3. Surfaces between geo acquisition and the clock-in mutation.
 * Sheet opens only when the org has at least one active job code; for
 * empty-list orgs the parent skips the sheet entirely (AC Scenario 3).
 *
 * Authoritative spec: docs/design/TIME-006-design.md §3.3.
 *
 * Behaviour:
 *   - Open: focus moves to the picker input; Tab cycles to Cancel/Confirm;
 *     Esc dismisses (treated as Cancel — no mutation).
 *   - Backdrop click dismisses without firing the mutation.
 *   - Confirm fires `onConfirm(jobCode | null)` — null when no selection (the
 *     server falls back to primaryRole per TIME-006-tech.md §5.1).
 *   - Reduced-motion: slide-up animation suppressed via CSS @media block.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Array} props.codes  — the cached active job-code list from parent
 * @param {boolean} props.submitting
 * @param {(code: string|null) => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
const ClockInJobCodeSheet = ({
  open,
  codes = [],
  submitting = false,
  onConfirm,
  onCancel,
}) => {
  const [pendingCode, setPendingCode] = useState(null);
  const confirmRef = useRef(null);

  // Reset selection each time the sheet opens.
  useEffect(() => {
    if (open) {
      setPendingCode(null);
    }
  }, [open]);

  // Esc handler.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !submitting) {
        e.preventDefault();
        if (typeof onCancel === "function") onCancel();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, submitting, onCancel]);

  if (!open) return null;

  const handleBackdropClick = () => {
    if (submitting) return;
    if (typeof onCancel === "function") onCancel();
  };

  const handleConfirm = () => {
    if (submitting) return;
    if (typeof onConfirm === "function") onConfirm(pendingCode || null);
  };

  return (
    <div
      className="cijcs-backdrop"
      role="presentation"
      onMouseDown={handleBackdropClick}
      data-testid="clock-in-job-code-sheet-backdrop"
    >
      <div
        className="cijcs-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cijcs-heading"
        // Stop the backdrop click handler when the user interacts inside the sheet.
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="clock-in-job-code-sheet"
      >
        <h2 id="cijcs-heading" className="cijcs-heading">
          Pick your job for this shift
        </h2>
        <p className="cijcs-helper">Optional — leave blank to use your primary role.</p>
        <div className="cijcs-picker-slot">
          <JobCodePicker
            testId="clock-in-job-code-picker"
            codes={codes}
            value={pendingCode}
            onChange={(code) => setPendingCode(code || null)}
            placeholder="Search or pick a job code..."
            emptyState="disabled-pill"
          />
        </div>
        <div className="cijcs-footer">
          <button
            type="button"
            className="cijcs-btn cijcs-btn--secondary"
            onClick={onCancel}
            disabled={submitting}
            data-testid="clock-in-job-code-sheet-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            ref={confirmRef}
            className="cijcs-btn cijcs-btn--primary"
            onClick={handleConfirm}
            disabled={submitting}
            data-testid="clock-in-job-code-sheet-confirm"
          >
            {submitting ? "Clocking in..." : "Clock in"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClockInJobCodeSheet;
